import ExportButton from 'components/ExportButton/ExportButton';
import UncontrolledPopover from 'components/SafeUncontrolledPopover';
import PropTypes from 'prop-types';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl';
import { Col, ModalBody, ModalHeader, Row } from 'reactstrap';
import { getFilterDisplayName } from 'utils/models/Filters';
import { createCSVFileName, stripHtml } from 'utils/util/util';
import Modal from '../../../components/SafeModal';
import {
  ENGAGEMENT_SURVEY_OTHERS_EXPLANATION,
  ICONS,
} from '../../../consts/consts';
import { capitalize } from '../../../utils/util/formatter';
import { createName, formatFilterList } from '../../EngagementSurvey/utils';
import AccordionTable from '../AccordionTable';
import ObjectsDropdown from '../Dropdowns/ObjectsDropdown';
import RichTextViewer from '../Inputs/RichTextViewer';

const csvFormatTextQuestion = (
  q,
  noFactorHeader: string,
  formatMessage: IntlShape['formatMessage']
) => {
  const groupings = Object.keys(q?.data || {});

  const dataRows = [];

  for (const group of groupings) {
    const groupNames = Object.keys(q?.data?.[group] || {});
    for (const groupName of groupNames) {
      for (const response of q?.data?.[group]?.[groupName]?.responses || []) {
        // @ts-expect-error
        dataRows.push([
          stripHtml(q?.displayName),
          q.factor ?? q.configs?.factor ?? '(No factor)',
          getFilterDisplayName(group, formatMessage),
          groupName,
          response,
        ]);
      }
    }
  }

  return dataRows;
};

const TextQuestionTableCard: FC<Props> = (props) => {
  const { formatMessage } = useIntl();
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);

  const toggleModal = useCallback(() => {
    setShowModal(!showModal);
  }, [showModal, setShowModal]);

  const setModal = useCallback(
    (data) => {
      setModalData(data);
      toggleModal();
    },
    [toggleModal, setModalData]
  );

  const createCsvData = useCallback(() => {
    let result: string[][] = [
      [`Question`, 'Factor', 'Field', 'Value', 'Response'],
    ];

    const noFactorHeader = formatMessage({
      id: 'app.views.widgets.cards.text_question_table_card.create_csv_data.no_factor',
      defaultMessage: '(No factor)',
    });

    props.rows.forEach((e) => {
      result = [
        ...result,
        ...csvFormatTextQuestion(e, noFactorHeader, formatMessage),
      ];
    });

    return result;
  }, [props.rows, formatMessage]);

  const exportButton = useMemo(
    () => (
      <span
        className="my-2"
        style={{ textTransform: 'initial', letterSpacing: 'initial' }}
      >
        <ExportButton filename={createCSVFileName()} data={createCsvData()} />
      </span>
    ),
    [createCsvData]
  );

  const COLUMNS = useMemo(
    () => [
      {
        name: props.title,
        field: 'name',
        className: 'col-9 align-self-center',
      },
      {
        name: exportButton,
        field: 'action',
        className: 'col-3 align-self-center text-end',
      },
    ],
    [props.title, exportButton]
  );

  const rowsWithFactorHeaders = useMemo(() => {
    if (!props.rows?.length) {
      return [];
    }

    // Group rows by factor
    const groupedByFactor: Record<string, object[]> = {};
    props.rows.forEach((q) => {
      if (q) {
        // @ts-expect-error
        const factor = q?.factor ?? '';
        if (!groupedByFactor[factor]) {
          groupedByFactor[factor] = [];
        }
        groupedByFactor[factor].push(q);
      }
    });

    const factorKeys = Object.keys(groupedByFactor).sort((a, b) => {
      // Sort by factor, with "" as the last factor
      if (a === '') return 1;
      if (b === '') return -1;
      return a.localeCompare(b);
    });

    let result: object[] = [];

    if (factorKeys.length === 1 && factorKeys[0] === '') {
      // No factors, just return the questions as is
      // @ts-expect-error
      result = props.rows || [];
    } else {
      const emptyFactorDisplay = formatMessage({
        id: 'app.views.widgets.cards.text_question_table_card.no_factor_header',
        defaultMessage: '(No factor)',
      });
      // Add factor headers and questions
      factorKeys.forEach((factor) => {
        const factorQuestions = groupedByFactor[factor];

        // Add factor header
        result.push({
          _isHeader: true,
          factor: factor || emptyFactorDisplay,
          displayName: factor || emptyFactorDisplay,
          count: 0, // No count displayed for factor headers
        });

        // Add questions in this factor
        result.push(...factorQuestions);
      });
    }

    return result;
  }, [props.rows, formatMessage]);

  const handleModalOpen = useCallback(
    (questionData: object) => {
      setModal({
        // @ts-expect-error
        name: questionData.displayName,
        // @ts-expect-error
        data: questionData.data,
      });
    },
    [setModal]
  );

  const handleModalOpenWrapper = useCallback(
    (q: object) => {
      return () => handleModalOpen(q);
    },
    [handleModalOpen]
  );

  const rows = useMemo(
    () =>
      rowsWithFactorHeaders?.map((q, i) => {
        // @ts-expect-error
        if (q._isHeader) {
          return {
            name: createName(
              formatMessage,
              // @ts-expect-error
              q.displayName,
              // @ts-expect-error
              q.count,
              'factor-header-' + i,
              'fs-3 fw-bold text-primary',
              100,
              true
            ),
            action: null,
            _isHeader: true,
          };
        }
        return {
          name: (
            <div className="ms-4">
              {createName(
                formatMessage,
                // @ts-expect-error
                q.displayName,
                // @ts-expect-error
                q.count,
                'text-' + i,
                'fs-4',
                100,
                false,
                // @ts-expect-error
                q.removed_count
              )}
            </div>
          ),
          action: (
            <>
              {
                // @ts-expect-error
                q.count ? (
                  <div
                    role="button"
                    onClick={handleModalOpenWrapper(q)}
                    className="btn btn-primary fs-4"
                  >
                    <FormattedMessage
                      id="app.views.widgets.cards.text_question_table_card.view_responses"
                      defaultMessage="View responses"
                    />
                  </div>
                ) : null
              }
            </>
          ),
        };
      }),
    [rowsWithFactorHeaders, handleModalOpenWrapper, formatMessage]
  );

  return (
    <>
      {showModal && (
        <TextQuestionModal
          // @ts-expect-error
          name={modalData.name}
          // @ts-expect-error
          data={modalData.data}
          isOpen={showModal}
          toggle={toggleModal}
          exportButton={props.exportButton}
          hideDropdown={props.hideDropdown ?? false}
          renderItem={props.renderItem}
        />
      )}
      <AccordionTable
        columns={COLUMNS}
        rows={rows}
        headerClassname="py-1 px-3 es-accordion-table-header "
      />
    </>
  );
};

const TextQuestionTableCard_propTypes = {
  title: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  exportButton: PropTypes.node,
  hideDropdown: PropTypes.bool,
  renderItem: PropTypes.func,
};

type Props = PropTypes.InferProps<typeof TextQuestionTableCard_propTypes>;

export default React.memo(TextQuestionTableCard);

const TextQuestionModal: FC<PropsQuestionModal> = (props) => {
  const { formatMessage, formatList } = useIntl();
  const [segment, setSegment] = useState(null);

  const segments = useMemo(
    () =>
      Object.keys(props.data)?.map((s) => ({
        id: s,
        name: s,
        hideNameColumn: s === 'overall',
      })),
    [props.data]
  );

  const helperText = useMemo(() => {
    if (segment) {
      // @ts-expect-error
      const segmentName = segment.name;
      if (segmentName === 'overall') {
        // @ts-expect-error
        const removedCount = props.data?.overall?.overall?.removed_count;
        if (removedCount) {
          return formatMessage(
            {
              id: 'app.views.widgets.cards.text_question_modal.removed_count_info',
              defaultMessage:
                '{removed} {removed, plural, one {response} other {responses}} were removed because their grouping was too small to be presented.',
            },
            { removed: removedCount }
          );
        }
      } else {
        const othersIncludedFilterValues =
          props.data?.[segmentName]?._others?.included_filter_values?.[
            segmentName
          ];
        if (othersIncludedFilterValues) {
          return formatMessage(
            {
              id: 'app.views.widgets.cards.text_question_modal.included_values_info',
              defaultMessage:
                'Others includes responses from {included_values})',
            },
            {
              included_values: formatList(
                formatFilterList(othersIncludedFilterValues, formatMessage),
                {
                  type: 'conjunction',
                }
              ),
            }
          );
        }
      }
    }
    return null;
  }, [segment, props.data, formatList, formatMessage]);

  useEffect(() => {
    if (segment === null) {
      // @ts-expect-error
      setSegment(segments[0]);
    }
  }, [segment, segments]);

  const handleSegmentChange = useCallback(
    (e) => {
      const match = segments.find((s) => s.id === e.target.value);
      // @ts-expect-error
      setSegment(match);
    },
    [segments]
  );

  const columns = useMemo(() => {
    // @ts-expect-error
    return segment?.hideNameColumn
      ? [
          {
            name: formatMessage({
              id: 'app.views.widgets.cards.text_question_table_card.response',
              defaultMessage: 'Response',
            }),
            field: 'value',
            className: 'col-12',
          },
        ]
      : [
          {
            // @ts-expect-error
            name: segment?.name,
            field: 'name',
            className: 'col-3',
          },
          {
            name: formatMessage({
              id: 'app.views.widgets.cards.text_question_table_card.response',
              defaultMessage: 'Response',
            }),
            field: 'value',
            className: 'col-9',
          },
        ];
  }, [segment, formatMessage]);

  const rows = useMemo(() => {
    // @ts-expect-error
    const selectedData = props.data[segment?.name];
    const dataRows = [];

    for (const field in selectedData) {
      const responses = selectedData[field]?.responses;

      responses.forEach((r, i) => {
        const sortKey = selectedData[field]?.sort_key;
        const id = 'text-modal-others-tootip-' + i;
        const fieldName =
          field === '_others' ? (
            <>
              <span id={id}>
                <FormattedMessage
                  id="app.views.widgets.cards.text_question_table_card.rows.others"
                  defaultMessage="Others"
                />
                <i
                  className={
                    ICONS.HELP + ' small ms-1 text-primary position-relative'
                  }
                />
              </span>
              <UncontrolledPopover
                placement="top"
                trigger="hover click"
                target={id}
              >
                {ENGAGEMENT_SURVEY_OTHERS_EXPLANATION(formatMessage)}
              </UncontrolledPopover>
            </>
          ) : (
            capitalize(field)
          );

        // @ts-expect-error
        dataRows.push({
          name: fieldName,
          rawName: sortKey ?? field,
          value: <RichTextViewer model={r} expanded={false} />,
        });
      });
    }

    // Sorting the responses alphabetically by segment value
    // (easier for manually scroll search)
    dataRows.sort((a, b) =>
      // @ts-expect-error
      a.rawName > b.rawName ? 1 : b.rawName > a.rawName ? -1 : 0
    );

    return dataRows;
  }, [props.data, segment, formatMessage]);

  return (
    <Modal
      autoFocus={false}
      isOpen={props.isOpen}
      toggle={props.toggle}
      size={'xl'}
    >
      <ModalHeader toggle={props.toggle}>
        <Row>
          <Col className="col-7 align-self-center">
            <Row>
              <Col className="text-muted col-auto">
                <FormattedMessage
                  id="app.views.widgets.cards.text_question_table_card.question"
                  defaultMessage="Question:"
                />
              </Col>
              <Col>
                {/* @ts-expect-error */}
                <RichTextViewer model={props.name} expanded={false} />
              </Col>
            </Row>
          </Col>
          <Col className="col-5">
            <Row className="justify-content-end">
              {!props.hideDropdown && (
                <Col className="col-auto">
                  <ObjectsDropdown
                    objects={segments}
                    // @ts-expect-error
                    value={segment?.id}
                    onChange={handleSegmentChange}
                    renderItem={props.renderItem}
                    size={'sm'}
                  />
                </Col>
              )}
              <Col className="col-auto me-2">{props.exportButton}</Col>
            </Row>
          </Col>
        </Row>
      </ModalHeader>
      <ModalBody>
        {helperText && <div className="mb-3">{helperText}</div>}
        <AccordionTable columns={columns} rows={rows} />
      </ModalBody>
    </Modal>
  );
};

const TextQuestionModal_propTypes = {
  name: PropTypes.string,
  data: PropTypes.object.isRequired,
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
  exportButton: PropTypes.node,
  hideDropdown: PropTypes.bool,
  renderItem: PropTypes.func,
};

type PropsQuestionModal = PropTypes.InferProps<
  typeof TextQuestionModal_propTypes
>;
